import {
	KnowledgeBaseArticle,
	KnowledgeBaseArticleContentItem,
	KnowledgeBaseArticleRating,
	KnowledgeBaseArticlePresentationResult,
	KnowledgeBaseArticleVisitDetails,
	KnowledgeBaseArticleInfo,
	KnowledgeBaseDirectory,
	KnowledgeBaseDirectoryLanguagesResult,
	KnowledgeBaseDirectoryTranslated,
	KnowledgeBaseArticleShortInfo,
	KnowledgeBaseArticleContentItemMeta,
	KnowledgeBaseDirectoryMeta,
	KnowledgeBaseLinkedIdArticleOptions,
	KnowledgeBaseArticleFlags,
	KnowledgeBaseRecommendedArticlesByCategory,
	KnowledgeBaseFlagedArticles,
} from 'types/KnowledgeBase';
import { serialize } from 'object-to-formdata';
import { httpClient } from './axiosInstance';

const FOR_PROGRAM = 'knowledge-base/admin/program';
const FOR_APPLICATION = 'knowledge-base/application';

export const knowledgeBaseApi = {
	// application
	application: {
		// getArticlesByTagLabels: (applicationId: number, labels: Array<string | number>) =>
		// 	httpClient.get<KnowledgeBaseTaggedArticles[]>(`${FOR_APPLICATION}/${applicationId}/article/tag/label`, {
		// 		labels,
		// 	}),
		getArticlesByFlag: (applicationId: number, flag: 'favorite' | 'toread' | 'readed') =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(`${FOR_APPLICATION}/${applicationId}/flagged/${flag}`),
		getFlagged: (applicationId: number) =>
			httpClient.get<KnowledgeBaseFlagedArticles>(`${FOR_APPLICATION}/${applicationId}/flagged`),
		getBlogArticles: (applicationId: number) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(`${FOR_APPLICATION}/${applicationId}/blog`),
		getProgramArticles: (applicationId: number) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(`${FOR_APPLICATION}/${applicationId}/program`),
		getHomepageRecommendedArticles: (applicationId: number) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(`${FOR_APPLICATION}/${applicationId}/homepage`),
		getRecommendedArticles: (applicationId: number) =>
			httpClient.get<KnowledgeBaseRecommendedArticlesByCategory>(
				`${FOR_APPLICATION}/${applicationId}/recommended`
			),
		getArticlesByLabelsOrIds: (applicationId: number, labels: Array<string | number>) =>
			httpClient.get<KnowledgeBaseArticleContentItemMeta[]>(`${FOR_APPLICATION}/${applicationId}/article/label`, {
				labels,
			}),
		getDirectoryByLabels: (applicationId: number, labels: string[]) =>
			httpClient.get<Record<string, KnowledgeBaseDirectoryMeta>>(
				`${FOR_APPLICATION}/${applicationId}/directory/label`,
				{ labels }
			),
		getDirectories: (applicationId: number) =>
			httpClient.get<KnowledgeBaseDirectoryTranslated[]>(`${FOR_APPLICATION}/${applicationId}/directory/list`),
		getArticlesByDirectory: (applicationId: number, directoryId: number, search?: string | null) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(
				`${FOR_APPLICATION}/${applicationId}/article/list/directory/${directoryId}`,
				{ search }
			),
		getArticlesByTag: (tagId: number, applicationId: number, search?: string | null) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(
				`${FOR_APPLICATION}/${applicationId}/article/list/tag/${tagId}`,
				{
					search,
				}
			),
		getArticlesFeatured: (applicationId: number) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(
				`${FOR_APPLICATION}/${applicationId}/article/list/featured`
			),
		getArticlesBySearch: (applicationId: number, search: string | null) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(
				`${FOR_APPLICATION}/${applicationId}/article/list/search`,
				{
					search,
				}
			),
		getArticlesForSession: (applicationId: number, sessionId: number) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(
				`${FOR_APPLICATION}/${applicationId}/article/list/session/${sessionId}`
			),
		getArticleContentRating: (contentId: number, applicationId: number) =>
			httpClient.get<KnowledgeBaseArticleRating | null>(
				`${FOR_APPLICATION}/${applicationId}/article-content/${contentId}/rating`
			),
		modifyArticleContentRating: (contentId: number, applicationId: number, rating: number) =>
			httpClient.post<KnowledgeBaseArticleRating | null>(
				`${FOR_APPLICATION}/${applicationId}/article-content/${contentId}/rating`,
				{
					rating,
				}
			),
		addArticleContentRatingComment: (contentId: number, applicationId: number, comment: string) =>
			httpClient.post<KnowledgeBaseArticleRating | null>(
				`${FOR_APPLICATION}/${applicationId}/article-content/${contentId}/rating`,
				{
					comment,
				}
			),
		getArticleContentFlags: (contentId: number, applicationId: number) =>
			httpClient.get<KnowledgeBaseArticleFlags>(
				`${FOR_APPLICATION}/${applicationId}/article-content/${contentId}/flags`
			),
		modifyArticleContentFlag: (contentId: number, applicationId: number, flags: Record<string, boolean>) =>
			httpClient.post<KnowledgeBaseArticleFlags>(
				`${FOR_APPLICATION}/${applicationId}/article-content/${contentId}/flags`,
				flags
			),
		readArticle: (contentId: number, applicationId: number, visitId: number) =>
			httpClient.post<boolean>(
				`${FOR_APPLICATION}/${applicationId}/article-content/${contentId}/read/${visitId}`
			),
		getArticleById: (applicationId: number, articleId: number, params?: Record<string, string | null>) =>
			httpClient.get<KnowledgeBaseArticlePresentationResult>(
				`${FOR_APPLICATION}/${applicationId}/article/${articleId}`,
				params
			),
		getArticleByLabel: (applicationId: number, label: string, params?: Record<string, string | null>) =>
			httpClient.get<KnowledgeBaseArticleShortInfo>(
				`${FOR_APPLICATION}/${applicationId}/article/label/${label}`,
				params
			),
	},
	program: {
		// program admin
		getLinkedInBadge: (programId: number) =>
			httpClient.get<KnowledgeBaseLinkedIdArticleOptions>(`${FOR_PROGRAM}/${programId}/linkedin-badge`),
		getDirectories: (programId: number) =>
			httpClient.get<KnowledgeBaseDirectoryTranslated[]>(`${FOR_PROGRAM}/${programId}/directory/list`),
		getArticlesForDirectory: (programId: number, directoryId: number, search?: string | null) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(
				`${FOR_PROGRAM}/${programId}/article/directory/${directoryId}`,
				{ search }
			),
		getArticlesForTag: (programId: number, tagId: number, search?: string | null) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(`${FOR_PROGRAM}/${programId}/article/tag/${tagId}`, {
				search,
			}),
		getProgramArticleByIdShow: (programId: number, articleId: number, params?: Record<string, string | null>) =>
			httpClient.get<KnowledgeBaseArticlePresentationResult>(
				`${FOR_PROGRAM}/${programId}/article/${articleId}/show`,
				params
			),
		getProgramArticlesBySearch: (programId: number, search?: string | null) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(`${FOR_PROGRAM}/${programId}/article/search`, {
				search,
			}),
		getProgramAvailableLangauges: (programId: number) =>
			httpClient.get<KnowledgeBaseDirectoryLanguagesResult>(`${FOR_PROGRAM}/${programId}/langs`),
		getProgramDirectory: (programId: number, directoryId: number) =>
			httpClient.get<KnowledgeBaseDirectory>(`${FOR_PROGRAM}/${programId}/directory/${directoryId}`),
		getProgramRecommendedArticles: (programId: number) =>
			httpClient.get<KnowledgeBaseRecommendedArticlesByCategory>(`${FOR_PROGRAM}/${programId}/recommended`),
		getArticlesByLabelsOrIds: (programId: number, labels: Array<string | number>) =>
			httpClient.get<KnowledgeBaseArticleContentItemMeta[]>(`${FOR_PROGRAM}/${programId}/article/label`, {
				labels,
			}),
		getDirectoryByLabels: (programId: number, labels: string[]) =>
			httpClient.get<Record<string, KnowledgeBaseDirectoryMeta>>(`${FOR_PROGRAM}/${programId}/directory/label`, {
				labels,
			}),
		getProgramOrganizersArticles: (programId: number) =>
			httpClient.get<KnowledgeBaseArticleContentItem[]>(`${FOR_PROGRAM}/${programId}/program-articles`),
		addProgramDirectory: (
			programId: number,
			translations: Array<{ language: string; name: string; slug: string }>
		) => httpClient.post(`${FOR_PROGRAM}/${programId}/directory`, serialize({ translations }, { indices: true })),
		editProgramDirectory: (
			programId: number,
			directoryId: number,
			translations: Array<{ language: string; name: string; slug: string }>
		) =>
			httpClient.put(
				`${FOR_PROGRAM}/${programId}/directory/${directoryId}`,
				serialize({ translations }, { indices: true })
			),
		removeProgramDirectory: (programId: number, directoryId: number) =>
			httpClient.delete(`${FOR_PROGRAM}/${programId}/directory/${directoryId}`),
		getProgramArticleById: (programId: number, articleId: number) =>
			httpClient.get<KnowledgeBaseArticle>(`${FOR_PROGRAM}/${programId}/article/${articleId}`),
		getProgramArticleInfo: (programId: number, articleId: number) =>
			httpClient.get<KnowledgeBaseArticleInfo>(`${FOR_PROGRAM}/${programId}/article/${articleId}/info`),
		getProgramArticleVisitsDetails: (programId: number, articleId: number) =>
			httpClient.get<KnowledgeBaseArticleVisitDetails[]>(
				`${FOR_PROGRAM}/${programId}/article/${articleId}/visits-details`
			),
		addProgramArticle: (programId: number, payload: any) =>
			httpClient.post<KnowledgeBaseArticlePresentationResult>(
				`${FOR_PROGRAM}/${programId}/article`,
				serialize(payload, { indices: true, nullsAsUndefineds: true })
			),
		editProgramArticle: (programId: number, articleId: number, payload: any) =>
			httpClient.put<KnowledgeBaseArticlePresentationResult>(
				`${FOR_PROGRAM}/${programId}/article/${articleId}`,
				serialize(payload, { indices: true, nullsAsUndefineds: true })
			),
		removeProgramArticle: (programId: number, articleId: number) =>
			httpClient.delete(`${FOR_PROGRAM}/${programId}/article/${articleId}`),

		uploadImage: (programId: number, file: File) =>
			httpClient.put<string>(`${FOR_PROGRAM}/${programId}/upload-image`, file),
	},
};
